import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  MoreHorizontal,
  X,
  ChevronRight,
  ChevronDown,
  UserCircle,
  LogOut,
  Megaphone,
  BookOpen,
  MessageSquare,
  Sparkles,
  Settings,
  ShieldCheck,
  Activity
} from 'lucide-react'

// Prioridade de exibição das guias primárias na barra inferior
const PRIMARY_PRIORITIES = [
  '/',
  '/chamados',
  '/atendimento',
  '/crm',
  '/hoje',
  '/financeiro',
  '/clientes',
  '/agenda',
  '/produtos',
  '/pessoas'
]

// Rótulos concisos e diretos para a barra inferior (Apple HIG: labels curtos sem truncamento)
const SHORT_LABELS = {
  '/': 'Dashboard',
  '/hoje': 'Hoje',
  '/crm': 'CRM',
  '/atendimento': 'Atendimento',
  '/chamados': 'Chamados',
  '/financeiro': 'Financeiro',
  '/clientes': 'Clientes',
  '/agenda': 'Agenda',
  '/rotinas': 'Rotinas',
  '/produtos': 'Estoque',
  '/pessoas/equipe': 'Pessoas',
  '/pessoas/ponto': 'Ponto',
  '/ajuda': 'Ajuda',
  '/fechamento': 'Faturas',
  '/senhas': 'Senhas'
}

export default function MobileBottomBar({
  navItems = [],
  user,
  roleLabel = 'Usuário',
  logout,
  features = {},
  onOpenChangelog
}) {
  const location = useLocation()
  const pathname = location.pathname
  const itemPath = (val) => val?.split('?')[0]

  const [sheetOpen, setSheetOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState({})
  const sheetRef = useRef(null)

  // Separar 4 abas primárias e os itens secundários para a aba "Mais"
  const { primaryItems, secondaryItems } = useMemo(() => {
    if (!navItems || !navItems.length) {
      return { primaryItems: [], secondaryItems: [] }
    }

    // Identificar itens por prioridade
    const sorted = [...navItems].sort((a, b) => {
      const idxA = PRIMARY_PRIORITIES.indexOf(itemPath(a.to))
      const idxB = PRIMARY_PRIORITIES.indexOf(itemPath(b.to))
      const weightA = idxA === -1 ? 999 : idxA
      const weightB = idxB === -1 ? 999 : idxB
      return weightA - weightB
    })

    const primary = sorted.slice(0, 4)
    const primaryPaths = new Set(primary.map((item) => itemPath(item.to)))

    // Itens secundários preservando a ordem original de navegação
    const secondary = navItems.filter((item) => !primaryPaths.has(itemPath(item.to)))

    return { primaryItems: primary, secondaryItems: secondary }
  }, [navItems])

  // Checar se a rota atual pertence a algum item das abas primárias
  const isPrimaryActive = (item) => {
    const p = itemPath(item.to)
    if (p === '/' && pathname === '/') return true
    if (p !== '/' && (pathname === p || pathname.startsWith(p + '/'))) return true
    if (item.children?.some((child) => pathname === itemPath(child.to) || (itemPath(child.to) !== '/' && pathname.startsWith(itemPath(child.to))))) {
      return true
    }
    return false
  }

  // Checar se a rota atual pertence a algum item secundário (ativa o botão "Mais")
  const isMoreActive = useMemo(() => {
    return secondaryItems.some((item) => {
      const p = itemPath(item.to)
      if (p === '/' && pathname === '/') return true
      if (p !== '/' && (pathname === p || pathname.startsWith(p + '/'))) return true
      if (item.children?.some((child) => pathname === itemPath(child.to) || (itemPath(child.to) !== '/' && pathname.startsWith(itemPath(child.to))))) {
        return true
      }
      return false
    }) || pathname === '/perfil' || pathname === '/configuracoes'
  }, [secondaryItems, pathname])

  const [isRendered, setIsRendered] = useState(sheetOpen)
  const [isClosing, setIsClosing] = useState(false)
  const closeTimerRef = useRef(null)

  const handleSmoothClose = useCallback(() => {
    if (isClosing) return
    setIsClosing(true)
    setSheetOpen(false)
    if (sheetRef.current) {
      sheetRef.current.style.transition = 'transform 0.22s cubic-bezier(0.32, 0.72, 0, 1)'
      sheetRef.current.style.transform = 'translateY(100%)'
    }
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => {
      setIsRendered(false)
      setIsClosing(false)
      if (sheetRef.current) {
        sheetRef.current.style.transform = ''
        sheetRef.current.style.transition = ''
      }
    }, 230)
  }, [isClosing])

  const handleOpen = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    setIsClosing(false)
    setIsRendered(true)
    setSheetOpen(true)
    if (sheetRef.current) {
      sheetRef.current.style.transform = ''
      sheetRef.current.style.transition = ''
    }
  }, [])

  // Fechar sheet suavemente ao navegar
  const prevPathRef = useRef(pathname)
  useEffect(() => {
    if (prevPathRef.current !== pathname) {
      prevPathRef.current = pathname
      if (isRendered) handleSmoothClose()
    }
  }, [pathname, isRendered, handleSmoothClose])

  useEffect(() => {
    if (!isRendered || isClosing) return undefined
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleSmoothClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isRendered, isClosing, handleSmoothClose])

  // Overscroll é contido via CSS no mobile-sheet-portal sem necessidade de mutar body.style.overflow (evita piscar de tela no iOS)

  // Gestos de arrasto (drag-to-dismiss) Apple HIG
  const dragRef = useRef({ startY: 0, currentY: 0, startTime: 0, isDragging: false })

  const handlePointerDown = (e) => {
    if (e.target.closest('button, a, input, select, textarea, .mobile-sheet-close, .mobile-sheet-backdrop')) return
    dragRef.current = {
      startY: e.clientY,
      currentY: e.clientY,
      startTime: Date.now(),
      isDragging: true
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (err) {
      console.debug('setPointerCapture failed:', err)
    }
  }

  const handlePointerMove = (e) => {
    if (!dragRef.current.isDragging) return
    const deltaY = e.clientY - dragRef.current.startY
    dragRef.current.currentY = e.clientY
    if (sheetRef.current) {
      if (deltaY > 0) {
        sheetRef.current.style.transform = `translateY(${deltaY}px)`
      } else {
        const rubber = deltaY * 0.25
        sheetRef.current.style.transform = `translateY(${rubber}px)`
      }
    }
  }

  const handlePointerEnd = (e) => {
    if (!dragRef.current.isDragging) return
    dragRef.current.isDragging = false
    try {
      if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
    } catch (err) {
      console.debug('releasePointerCapture failed:', err)
    }

    const deltaY = dragRef.current.currentY - dragRef.current.startY
    const elapsed = Math.max(1, Date.now() - dragRef.current.startTime)
    const velocity = deltaY / elapsed

    if (deltaY > 110 || velocity > 0.35) {
      handleSmoothClose()
    } else {
      if (sheetRef.current) {
        sheetRef.current.style.transition = 'transform 0.22s var(--ease-apple-standard)'
        sheetRef.current.style.transform = 'translateY(0px)'
        setTimeout(() => {
          if (sheetRef.current) sheetRef.current.style.transition = ''
        }, 220)
      }
    }
  }

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const initials = user?.nome
    ?.split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'AD'

  return (
    <>
      {/* Barra de Navegação Inferior Fixa */}
      <nav
        className="mobile-bottom-bar"
        aria-label="Navegação móvel principal"
        role="navigation"
      >
        <div className="mobile-bottom-bar__inner">
          {primaryItems.map((item) => {
            const Icon = item.icon
            const active = isPrimaryActive(item)
            const label = SHORT_LABELS[itemPath(item.to)] || item.label
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`mobile-bottom-bar__tab active:scale-[0.95] transition-[transform,color] duration-150 ${active ? 'is-active' : ''}`}
                aria-current={active ? 'page' : undefined}
                title={item.label}
              >
                <div className="mobile-bottom-bar__icon-wrapper">
                  <Icon size={20} strokeWidth={active ? 2.25 : 1.75} aria-hidden="true" />
                  {active && <span className="mobile-bottom-bar__active-indicator" />}
                </div>
                <span className="mobile-bottom-bar__label">{label}</span>
              </Link>
            )
          })}

          {/* 5ª Aba: Mais (Menu Completo) */}
          <button
            type="button"
            className={`mobile-bottom-bar__tab mobile-bottom-bar__tab--more active:scale-[0.95] transition-[transform,color] duration-150 ${
              isMoreActive || sheetOpen ? 'is-active' : ''
            }`}
            onClick={() => {
              if (sheetOpen) handleSmoothClose()
              else handleOpen()
            }}
            aria-expanded={sheetOpen}
            aria-controls="mobile-more-sheet"
            aria-label="Abrir menu com mais opções"
          >
            <div className="mobile-bottom-bar__icon-wrapper">
              <MoreHorizontal size={20} strokeWidth={isMoreActive ? 2.25 : 1.75} aria-hidden="true" />
              {isMoreActive && <span className="mobile-bottom-bar__active-indicator" />}
            </div>
            <span className="mobile-bottom-bar__label">Mais</span>
          </button>
        </div>
      </nav>

      {/* Bottom Sheet "Mais" com todos os módulos secundários e perfil */}
      {isRendered && (
        <div className="mobile-sheet-portal" role="dialog" aria-modal="true" id="mobile-more-sheet">
          <div
            className={`mobile-sheet-backdrop ${isClosing ? 'is-exiting' : ''}`}
            onClick={handleSmoothClose}
            aria-label="Fechar menu"
          />
          <div
            ref={sheetRef}
            className={`mobile-sheet-panel ${isClosing ? 'is-exiting' : 'is-entering'}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
          >
            {/* Handle do topo da folha com área tátil confortável */}
            <div className="mobile-sheet-handle-zone cursor-grab active:cursor-grabbing py-2">
              <div className="mobile-sheet-handle" />
            </div>

            {/* Cabeçalho da folha: Perfil e Fechar */}
            <header className="mobile-sheet-header">
              <div className="mobile-sheet-user">
                <span className="mobile-sheet-avatar">{initials}</span>
                <div className="mobile-sheet-user-info">
                  <strong>{user?.nome || 'Usuário'}</strong>
                  <small>{roleLabel}</small>
                </div>
              </div>
              <button
                type="button"
                className="mobile-sheet-close active:scale-[0.97] transition-transform duration-150"
                onClick={handleSmoothClose}
                aria-label="Fechar menu"
              >
                <X size={18} />
              </button>
            </header>

            {/* Conteúdo com scroll seguro */}
            <div className="mobile-sheet-content">
              {/* Seções de navegação secundária */}
              {secondaryItems.length > 0 && (
                <div className="mobile-sheet-section">
                  <span className="mobile-sheet-section-title">Módulos do Sistema</span>
                  <div className="mobile-sheet-list">
                    {secondaryItems.map((item) => {
                      const Icon = item.icon
                      const active = isPrimaryActive(item)
                      const hasChildren = Boolean(item.children?.length)
                      const isExpanded = expandedGroups[item.to] ?? active

                      return (
                        <div key={item.to} className="mobile-sheet-item-wrapper">
                          {hasChildren ? (
                            <>
                              <button
                                type="button"
                                className={`mobile-sheet-link mobile-sheet-link--has-children ${active ? 'is-active' : ''}`}
                                onClick={() => toggleGroup(item.to)}
                                aria-expanded={isExpanded}
                              >
                                <div className="mobile-sheet-link-left">
                                  <Icon size={18} aria-hidden="true" />
                                  <span>{item.label}</span>
                                </div>
                                <ChevronDown
                                  size={16}
                                  className={`mobile-sheet-chevron ${isExpanded ? 'is-expanded' : ''}`}
                                  aria-hidden="true"
                                />
                              </button>
                              {isExpanded && (
                                <div className="mobile-sheet-subnav">
                                  {item.children.map((child) => {
                                    const ChildIcon = child.icon
                                    const childActive = pathname === itemPath(child.to)
                                    return (
                                      <Link
                                        key={child.to}
                                        to={child.to}
                                        className={`mobile-sheet-sublink ${childActive ? 'is-active' : ''}`}
                                        aria-current={childActive ? 'page' : undefined}
                                        onClick={handleSmoothClose}
                                      >
                                        <ChildIcon size={16} aria-hidden="true" />
                                        <span>{child.label}</span>
                                      </Link>
                                    )
                                  })}
                                </div>
                              )}
                            </>
                          ) : (
                            <Link
                              to={item.to}
                              className={`mobile-sheet-link ${active ? 'is-active' : ''}`}
                              aria-current={active ? 'page' : undefined}
                              onClick={handleSmoothClose}
                            >
                              <div className="mobile-sheet-link-left">
                                <Icon size={18} aria-hidden="true" />
                                <span>{item.label}</span>
                              </div>
                              <ChevronRight size={16} className="text-muted-foreground" aria-hidden="true" />
                            </Link>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Utilidades e Ações Rápidas */}
              <div className="mobile-sheet-section">
                <span className="mobile-sheet-section-title">Minha Conta & Preferências</span>
                <div className="mobile-sheet-list">
                  <Link
                    to="/perfil"
                    className="mobile-sheet-link"
                    onClick={handleSmoothClose}
                  >
                    <div className="mobile-sheet-link-left">
                      <UserCircle size={18} aria-hidden="true" />
                      <span>Meu Perfil e Preferências</span>
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground" aria-hidden="true" />
                  </Link>

                  {features.experiencia?.enabled && (
                    <>
                      <button
                        type="button"
                        className="mobile-sheet-link"
                        onClick={() => {
                          handleSmoothClose()
                          window.dispatchEvent(
                            new CustomEvent('aden:open-experience', { detail: 'help' })
                          )
                        }}
                      >
                        <div className="mobile-sheet-link-left">
                          <BookOpen size={18} aria-hidden="true" />
                          <span>Ajuda e Atalhos</span>
                        </div>
                        <ChevronRight size={16} className="text-muted-foreground" aria-hidden="true" />
                      </button>

                      <button
                        type="button"
                        className="mobile-sheet-link"
                        onClick={() => {
                          handleSmoothClose()
                          window.dispatchEvent(
                            new CustomEvent('aden:open-experience', { detail: 'feedback' })
                          )
                        }}
                      >
                        <div className="mobile-sheet-link-left">
                          <MessageSquare size={18} aria-hidden="true" />
                          <span>Enviar Sugestão</span>
                        </div>
                        <ChevronRight size={16} className="text-muted-foreground" aria-hidden="true" />
                      </button>
                    </>
                  )}

                  {onOpenChangelog && (
                    <button
                      type="button"
                      className="mobile-sheet-link"
                      onClick={() => {
                        handleSmoothClose()
                        onOpenChangelog()
                      }}
                    >
                      <div className="mobile-sheet-link-left">
                        <Megaphone size={18} aria-hidden="true" />
                        <span>Novidades da Versão</span>
                      </div>
                      <ChevronRight size={16} className="text-muted-foreground" aria-hidden="true" />
                    </button>
                  )}

                  {/* Sair do Sistema */}
                  <button
                    type="button"
                    className="mobile-sheet-link mobile-sheet-link--danger"
                    onClick={() => {
                      handleSmoothClose()
                      logout?.()
                    }}
                  >
                    <div className="mobile-sheet-link-left">
                      <LogOut size={18} aria-hidden="true" />
                      <span>Sair da Conta</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
